import classNames from 'classnames';
import { LinkFactoryOnClick } from 'common/link/link-on-click.js';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import type { NavigateFunction } from 'react-router-dom';
import { IWarning } from 'redux-state/weather-warnings/types.js';
import { IMarker, getMarkerVersionPosition, markerLabel } from 'types/markerCore/marker-core-type.js';
import { DEFAULT_MAP_PIN_CLASSNAME } from 'utilities/weather-station/location.js';
import { getAdditionalMarkerContent } from '../additional-content/utils.js';
import { HTMLMarkerCore } from './html-marker-core.js';
import { getHTMLMarkerIcons } from './html-marker-icons.js';
import { getTextAlign } from './html-utils.js';

/** Within leaflet we do not want to redirect links as they cause problems in routing */
// export const IGNORE_LINK_CAPTURE = 'data-ignore-link-capture'

interface IHTMLMarkerProps {
  marker?: IMarker;
  selected?: boolean;
  isLarge: boolean;
  warnings?: IWarning[];
  navigate: NavigateFunction;
}

export const HTMLMarker = ({ marker, selected, isLarge, warnings, navigate }: IHTMLMarkerProps): JSX.Element => {
  const link = typeof marker?.action === 'string' ? marker?.action : undefined;
  const onClick = link ? () => navigate(link) : undefined;
  const [hover, setHover] = useState(false);

  const [lclMarkerLabel, setMarkersLabel] = useState(markerLabel.viewport({ isLarge, marker }));

  const icons = getHTMLMarkerIcons({ marker: marker, warnings });

  const onOverHandler = useCallback(
    (over: boolean) => () => {
      setHover(over);
    },
    [],
  );

  useEffect(() => {
    if (hover) {
      setMarkersLabel(markerLabel.hover(marker));
    } else {
      setMarkersLabel(markerLabel.viewport({ isLarge, marker }));
    }
  }, [hover, isLarge, marker]);

  const markerContent = getAdditionalMarkerContent(marker);

  const townSelector = (link ?? '').match(/\/([^/]+)\/?$/)?.[1];

  const townId = townSelector ? 'town-' + townSelector : undefined;

  // @NOTE: AnchorVsButton. In map component where these markers are being used, if the marker needs
  // to perform an action then an onClick event is added to the leafletMarker which is the parent of
  // our markers. This enables us to handle onClick events in the map component instead of passing them into the markers here.
  // If the marker doesn't have an action it SHOULD have a link, so NO onClick event is added, and the link
  // is passed into our <a> tag's href below which is handled normally.
  // The content that sits after the label
  const labelPos = getMarkerVersionPosition(marker).labelPos;
  const textAlign = getTextAlign(labelPos);

  return (
    <div
      data-module="html-marker"
      className={classNames(DEFAULT_MAP_PIN_CLASSNAME, {
        [`is-${labelPos}`]: !!labelPos,
        'is-selected': selected,
      })}
      style={marker && getMarkerVersionPosition(marker).static}
    >
      <div
        onMouseEnter={onOverHandler(true)}
        onMouseLeave={onOverHandler(false)}
        onFocus={onOverHandler(true)}
        onBlur={onOverHandler(false)}
        className={classNames('Map-tooltip--position u-posAbs', textAlign)}
        style={{ width: '240px' }}
      >
        <LclLink onClick={onClick} townId={townId}>
          <HTMLMarkerCore label={lclMarkerLabel} icons={icons} markerContent={markerContent} />
        </LclLink>
      </div>
    </div>
  );
};

const LclLink = ({
  onClick,
  townId,
  children,
}: {
  onClick?: () => void;
  townId: string | undefined;
  children: ReactNode;
}) => {
  if (onClick) {
    return (
      <LinkFactoryOnClick
        onClick={onClick}
        className={classNames('Map-tooltip-content bg-white-85 u-inlineBlock', { 'town-marker-anchor': townId })}
        id={townId}
      >
        {children}
      </LinkFactoryOnClick>
    );
  }
  return (
    <span
      className={classNames('Map-tooltip-content bg-white-85 u-inlineBlock', { 'town-marker-anchor': townId })}
      id={townId}
    >
      {children}
    </span>
  );
};
